'use client';

import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { de } from './messages/de.js';
import { en } from './messages/en.js';
import {
  DEFAULT_LOCALE,
  INTL_LOCALES,
  detectLocale,
  writeLocaleCookie,
  type Locale,
} from './locale.js';

/** The German catalogue is the source of truth; English must mirror its shape. */
export type Messages = typeof de;

/** Dot paths into the catalogue, so a typo in a key fails to compile. */
export type MessageKey = Paths<Messages>;

type Paths<T> = T extends string
  ? never
  : {
      [K in keyof T & string]: T[K] extends string ? K : `${K}.${Paths<T[K]>}`;
    }[keyof T & string];

const CATALOGUES: Record<Locale, Messages> = { de, en };

export type TranslateVars = Record<string, string | number>;

/** `t('leads.count', { n: 3 })` fills `{n}` in "Noch {n} offen". */
export type Translate = (key: MessageKey, vars?: TranslateVars) => string;

function lookup(messages: Messages, key: string): string | undefined {
  let node: unknown = messages;
  for (const segment of key.split('.')) {
    if (typeof node !== 'object' || node === null) return undefined;
    node = (node as Record<string, unknown>)[segment];
  }
  return typeof node === 'string' ? node : undefined;
}

function interpolate(template: string, vars?: TranslateVars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}

export interface LocaleContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: Translate;
  /** BCP 47 tag for Intl formatters. */
  intlLocale: string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export interface LocaleProviderProps {
  children: ReactNode;
  /**
   * Locale the server already rendered with. Passing it keeps the first client
   * render identical to the server's markup; without it the provider would
   * detect a different locale after hydration and React would complain.
   */
  initialLocale?: Locale;
}

export function LocaleProvider({ children, initialLocale }: LocaleProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale ?? DEFAULT_LOCALE);

  useEffect(() => {
    // Only when the server did not already decide: a first-time visitor whose
    // browser asks for English should get English without touching the toggle.
    if (initialLocale) return;
    const detected = detectLocale();
    if (detected !== locale) setLocaleState(detected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLocale]);

  // `lang` drives screen-reader pronunciation and browser translation prompts,
  // so it has to track the rendered language whether the visitor chose it or it
  // was detected from their browser — not only when the toggle is used.
  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: Locale): void => {
    writeLocaleCookie(next);
    setLocaleState(next);
  }, []);

  const t = useCallback<Translate>(
    (key, vars) => {
      const messages = CATALOGUES[locale];
      // Fall back to German rather than showing a raw key: a missing English
      // string should degrade to readable text, not to "leads.title".
      const template = lookup(messages, key) ?? lookup(CATALOGUES[DEFAULT_LOCALE], key) ?? key;
      return interpolate(template, vars);
    },
    [locale],
  );

  const value = useMemo<LocaleContextValue>(
    () => ({ locale, setLocale, t, intlLocale: INTL_LOCALES[locale] }),
    [locale, setLocale, t],
  );

  return createElement(LocaleContext.Provider, { value }, children);
}

export function useLocale(): LocaleContextValue {
  const value = useContext(LocaleContext);
  if (!value) throw new Error('useLocale must be used inside a <LocaleProvider>');
  return value;
}

/** Shorthand for the common case of needing only the translate function. */
export function useT(): Translate {
  return useLocale().t;
}
